import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    ConversionDal,
    PluginContext,
    ReadOnlyExtensionAPI
} from '../../types'
import {START, END, CENTER} from './constants'
import type {Pointer} from '@wix/document-services-types'
import {
    isInternalBox,
    isSuperGridCell,
    isPlugin,
    updateTargetLayout,
    isMenuContainerChild,
    isHeaderFirstRowChild,
    isChildOfStackContainer,
    getItemLayoutType,
    shouldBeHorizontallyStretched
} from '../common'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {getStructurePointer, getTargetLayoutPointer, getStylePointer} from '../../conversionDal/utils/pointer'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {ComponentAlignment} from './types'
import _ from 'lodash'
import type {StyleEntryItem} from '../../conversionDal/types'
// eslint-disable-next-line wix-editor/no-internal-import
import {CSS_PROPERTIES} from '@wix/component-protocol/schema'
import {Orientation} from '../../enums'

const updateTextAlignProperties = (
    conversionDal: ConversionDal,
    compStylePointer: Pointer,
    baseStyleSettings: StyleEntryItem,
    alignment: ComponentAlignment
) => {
    if (
        baseStyleSettings?.cssProperties?.textAlign ||
        baseStyleSettings?.metaData?.default?.cssProperties?.hasOwnProperty?.('textAlign')
    ) {
        conversionDal.modify(compStylePointer, (value: any) =>
            _.merge({}, value, {
                cssProperties: {
                    textAlign: alignment
                }
            })
        )
    }

    const customPropertiesInfo = baseStyleSettings?.metaData?.cssCustomPropertiesInfo || {}

    Object.entries(customPropertiesInfo).forEach(([customPropKey, propertyType]) => {
        if (propertyType === CSS_PROPERTIES.CSS_PROPERTY_TYPE.textAlign) {
            conversionDal.modify(compStylePointer, (value: any) =>
                _.merge({}, value, {
                    cssCustomProperties: {
                        [customPropKey]: alignment
                    }
                })
            )
        }
    })
}

const updateComponentAlignment = (
    componentStageData: ComponentStageData,
    alignment: ComponentAlignment,
    orientation: Orientation
): void => {
    const {componentPointer, conversionDal} = componentStageData

    const {itemLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer)))
    const alignmentProperty = orientation === Orientation.Horizontal ? 'justifySelf' : 'alignSelf'

    const itemLayoutType = getItemLayoutType(componentStageData)

    updateTargetLayout(componentStageData, {
        itemLayout: {type: itemLayoutType, ...itemLayout, [alignmentProperty]: alignment}
    })
}

const updateComponentVerticalAlignment = (
    componentStageData: ComponentStageData,
    alignment: ComponentAlignment
): void => {
    updateComponentAlignment(componentStageData, alignment, Orientation.Vertical)
}

const updateComponentHorizontalAlignment = (
    componentStageData: ComponentStageData,
    alignment: ComponentAlignment
): void => {
    const {componentPointer, conversionDal, initialSnapshot} = componentStageData
    const {semanticType} = conversionDal.get(componentPointer)
    updateComponentAlignment(componentStageData, alignment, Orientation.Horizontal)
    if (semanticType === SemanticTypes.TEXT) {
        const compStylePointer = getStylePointer(componentPointer)
        const baseStyleSettings = initialSnapshot.getValue(compStylePointer) as StyleEntryItem

        if (baseStyleSettings) {
            updateTextAlignProperties(conversionDal, compStylePointer, baseStyleSettings, alignment)
        }
    }
}

const checkParentAlignment = (
    componentPointer: Pointer,
    conversionDal: ConversionDal,
    alignmentDirection: ComponentAlignment
): boolean => {
    const {parent} = conversionDal.get(componentPointer)
    const {alignment} = conversionDal.get(getStructurePointer({id: parent})) ?? {alignment: CENTER}
    return alignment === alignmentDirection
}

export const horizontalAlignmentHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isMenuContainerChild(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData) => {
            updateComponentHorizontalAlignment(componentStageData, CENTER)
        },
        heuristicCode: 'MENU CONTAINER CHILD ALIGNMENT'
    },
    {
        predicate: (
            componentStageData: ComponentStageData,
            contextHelper?: PluginContext,
            readOnlyExtensionApi?: ReadOnlyExtensionAPI
        ) => {
            const {componentPointer, config, conversionDal} = componentStageData
            return (
                componentPointer.id === config.rootPointer.id ||
                isInternalBox(componentStageData) ||
                isSuperGridCell(componentStageData) ||
                isPlugin(componentStageData) ||
                isHeaderFirstRowChild(componentPointer, conversionDal) ||
                isChildOfStackContainer(componentStageData) ||
                shouldBeHorizontallyStretched(componentStageData, contextHelper, readOnlyExtensionApi)
            )
        },
        apply: () => {}
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return checkParentAlignment(componentPointer, conversionDal, START)
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHorizontalAlignment(componentStageData, START),
        heuristicCode: 'COMPONENTS ALIGNED START'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return checkParentAlignment(componentPointer, conversionDal, END)
        },
        apply: (componentStageData: ComponentStageData) => updateComponentHorizontalAlignment(componentStageData, END),
        heuristicCode: 'COMPONENTS ALIGNED END'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return checkParentAlignment(componentPointer, conversionDal, CENTER)
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHorizontalAlignment(componentStageData, CENTER),
        heuristicCode: 'COMPONENTS MIXED ALIGNMENT'
    }
]

export const verticalAlignmentHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return isHeaderFirstRowChild(componentPointer, conversionDal)
        },
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalAlignment(componentStageData, CENTER)
        },
        heuristicCode: 'COMPONENT IN HEADER FIRST ROW'
    }
]
